import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Flower2, Sparkles, ArrowRight } from 'lucide-react';

const FloralJewellerySection = ({ scrollToSection }) => {
  return (
    <section className="py-24 bg-gradient-to-b from-white to-rose-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <Flower2 className="w-12 h-12 text-rose-500 mx-auto mb-4" />
          <p className="text-rose-500 text-sm font-medium tracking-wider uppercase mb-2">Handcrafted Beauty</p>
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-stone-800 mb-4">Floral Jewellery</h2>
          <p className="text-stone-600 max-w-2xl mx-auto">
            We offer beautifully handcrafted fresh and artificial flower jewellery for all your special occasions. 
            Every piece is customized to match your outfit, theme, and personal style.
          </p>
        </div>

        <div className="max-w-6xl mx-auto mb-16">
          <div className="bg-rose-50 rounded-2xl p-8 mb-12 border border-rose-200">
            <h3 className="text-2xl font-semibold text-stone-800 mb-6 text-center">Available For:</h3>
            <div className="grid md:grid-cols-3 gap-4">
              {[
                'Haldi Ceremony',
                'Mehendi Function',
                'Baby Shower',
                'Wedding Day Garlands',
                'Hair Accessories',
                'Floral Dupattas & Shelas'
              ].map((item, index) => (
                <div key={index} className="flex items-center space-x-2 bg-white p-3 rounded-lg">
                  <Flower2 className="w-5 h-5 text-rose-500 flex-shrink-0" />
                  <span className="text-stone-700">{item}</span>
                </div>
              ))}
            </div>

            <div className="mt-6 pt-6 border-t border-rose-200/60 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white/70 p-4 rounded-xl">
              <div>
                <p className="font-semibold text-stone-900 text-sm sm:text-base flex items-center">
                  <Sparkles className="w-4 h-4 text-rose-500 mr-2" />
                  Looking for our full Jewellery & Accessories catalog?
                </p>
                <p className="text-xs sm:text-sm text-stone-600">
                  Browse 116+ pieces with direct WhatsApp cart inquiry.
                </p>
              </div>
              <Link to="/my-collection">
                <Button className="bg-rose-600 hover:bg-rose-700 text-white text-xs sm:text-sm whitespace-nowrap shadow-sm">
                  Explore Full Collection <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Dupattas */}
          <div className="mb-12">
            <h3 className="text-3xl font-serif font-bold text-stone-800 mb-8 text-center">Dupattas & Shelas</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Card className="border-rose-200 hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <h4 className="font-semibold text-stone-800 mb-2">Radhika Merchant Style Floral Dupatta</h4>
                  <p className="text-sm text-stone-600 mb-3">2–6 ft | With Jendhu Flowers or Roses</p>
                  <p className="text-2xl font-bold text-rose-500 mb-4">₹15,000</p>
                  <Button onClick={() => scrollToSection('contact')} className="w-full bg-rose-500 hover:bg-rose-600">Order Now</Button>
                </CardContent>
              </Card>

              <Card className="border-rose-200 hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <h4 className="font-semibold text-stone-800 mb-2">Floral Shela - Plain</h4>
                  <p className="text-sm text-stone-600 mb-3">2–5 ft</p>
                  <p className="text-2xl font-bold text-rose-500 mb-4">₹11,000</p>
                  <Button onClick={() => scrollToSection('contact')} className="w-full bg-rose-500 hover:bg-rose-600">Order Now</Button>
                </CardContent>
              </Card>

              <Card className="border-rose-200 hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <h4 className="font-semibold text-stone-800 mb-2">Floral Shela with Rose Petal Border</h4>
                  <p className="text-sm text-stone-600 mb-3">2–5 ft | Custom borders available</p>
                  <p className="text-2xl font-bold text-rose-500 mb-4">₹16,000</p>
                  <Button onClick={() => scrollToSection('contact')} className="w-full bg-rose-500 hover:bg-rose-600">Order Now</Button>
                </CardContent>
              </Card>

              <Card className="border-rose-200 hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <h4 className="font-semibold text-stone-800 mb-2">Dupatta Style Odhani - Plain</h4>
                  <p className="text-sm text-stone-600 mb-3">1.5–5 ft</p>
                  <p className="text-2xl font-bold text-rose-500 mb-4">₹10,000</p>
                  <Button onClick={() => scrollToSection('contact')} className="w-full bg-rose-500 hover:bg-rose-600">Order Now</Button>
                </CardContent>
              </Card>

              <Card className="border-rose-200 hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <h4 className="font-semibold text-stone-800 mb-2">Dupatta Style Odhani with Border</h4>
                  <p className="text-sm text-stone-600 mb-3">1.5–5 ft | With Rose Petal Border</p>
                  <p className="text-2xl font-bold text-rose-500 mb-4">₹15,000</p>
                  <Button onClick={() => scrollToSection('contact')} className="w-full bg-rose-500 hover:bg-rose-600">Order Now</Button>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Jewellery Sets */}
          <div>
            <h3 className="text-3xl font-serif font-bold text-stone-800 mb-8 text-center">Jewellery Sets</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Card className="border-rose-200 hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <h4 className="font-semibold text-stone-800 mb-2">Complete Jewellery Set</h4>
                  <p className="text-xs text-stone-600 mb-3">Bindhi • Earrings with Jhumka & Velvet • Long Necklace • Kamar Patta • Bangles with Ring • Kalerey • Aai-Baba Brooch</p>
                  <p className="text-2xl font-bold text-rose-500 mb-2">₹7,500</p>
                  <p className="text-sm text-stone-600 mb-4">Tagar + Any Flowers</p>
                  <Button onClick={() => scrollToSection('contact')} className="w-full bg-rose-500 hover:bg-rose-600">Order Now</Button>
                </CardContent>
              </Card>

              <Card className="border-rose-200 hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <h4 className="font-semibold text-stone-800 mb-2">Full Set (Tagar + Stones)</h4>
                  <p className="text-xs text-stone-600 mb-3">Bindhi • Earrings with Jhumka & Velvet • Long Necklace • Kamar Patta • Bangles with Ring • Kalerey • Aai-Baba Brooch</p>
                  <p className="text-2xl font-bold text-rose-500 mb-2">₹7,000</p>
                  <p className="text-sm text-stone-600 mb-4">Tagar + Stones Only</p>
                  <Button onClick={() => scrollToSection('contact')} className="w-full bg-rose-500 hover:bg-rose-600">Order Now</Button>
                </CardContent>
              </Card>

              <Card className="border-rose-200 hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <h4 className="font-semibold text-stone-800 mb-2">Minimal Set</h4>
                  <p className="text-xs text-stone-600 mb-3">Bindhi • Earrings with Jhumka • Long Necklace • Mekhala • Bangles with One Ring • Simple Kalerey</p>
                  <p className="text-2xl font-bold text-rose-500 mb-4">₹6,000</p>
                  <Button onClick={() => scrollToSection('contact')} className="w-full bg-rose-500 hover:bg-rose-600">Order Now</Button>
                </CardContent>
              </Card>

              <Card className="border-rose-200 hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <h4 className="font-semibold text-stone-800 mb-2">Basic Set</h4>
                  <p className="text-xs text-stone-600 mb-3">Long Necklace • Kamar Patta • Bangles • Earrings or Bindhi</p>
                  <p className="text-2xl font-bold text-rose-500 mb-2">₹5,500</p>
                  <p className="text-sm text-stone-600 mb-4">Any 3–4 Items</p>
                  <Button onClick={() => scrollToSection('contact')} className="w-full bg-rose-500 hover:bg-rose-600">Order Now</Button>
                </CardContent>
              </Card>

              <Card className="border-rose-200 hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <h4 className="font-semibold text-stone-800 mb-2">Haldi / Mehendi Set</h4>
                  <p className="text-xs text-stone-600 mb-3">Bindhi • Earrings with Jhumka Velvet • Long Necklace • Bangles with Ring • Kalerey</p>
                  <p className="text-2xl font-bold text-rose-500 mb-2">₹5,000 - 8,000</p>
                  <p className="text-sm text-stone-600 mb-4">Customizable</p>
                  <Button onClick={() => scrollToSection('contact')} className="w-full bg-rose-500 hover:bg-rose-600">Order Now</Button>
                </CardContent>
              </Card>

              <Card className="border-rose-200 hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <h4 className="font-semibold text-stone-800 mb-2">Wedding Garlands</h4>
                  <p className="text-xs text-stone-600 mb-3">Customizable size and flower types</p>
                  <p className="text-2xl font-bold text-rose-500 mb-2">₹7,000 - 25,000</p>
                  <p className="text-sm text-stone-600 mb-4">Per Pair</p>
                  <Button onClick={() => scrollToSection('contact')} className="w-full bg-rose-500 hover:bg-rose-600">Order Now</Button>
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="mt-12 bg-rose-50 rounded-2xl p-6 border border-rose-200">
            <h3 className="text-lg font-semibold text-stone-800 mb-4">Customization & Notes:</h3>
            <ul className="space-y-2 text-stone-600">
              <li>• We help design your floral jewellery to perfectly match your outfit, color theme, and bridal look</li>
              <li>• Final cost may vary depending on the flowers used, design complexity, and specific requirements</li>
              <li>• Travel charges are additional</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FloralJewellerySection;